import { Link } from "react-router-dom";
import FadeInView from "./FadeInView";
import Typewriter from "./Typewriter";

/* ── ICONS ── */
const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M7 17 17 7M7 7h10v10" />
  </svg>
);
const IconExternal = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

/* ── DATA ── */
const PROJECTS = [
  { name: "AyurSutra", desc: "Ayurvedic Health & Therapy Platform — connect with certified practitioners for holistic healing and wellness.", link: "https://ayursu.vercel.app/", tag: "Wellness", image: "/img/AyurSutra.png" },
  { name: "DevIQ", desc: "DevIQ unifies your GitHub, LeetCode, and Codeforces stats into a single score — with AI insights, contribution tracking, and head-to-head comparisons.", link: "https://deviq.online/", tag: "Tool", image: "/img/Deviq.png" },
  { name: "Chess Game", desc: "Fully playable browser chess with move validation and AI opponent logic.", link: "https://saketkhundia.github.io/chess-game", tag: "Game", image: "/img/chess game.png" },
  { name: "Calculator App", desc: "Clean, functional calculator with expression parsing and history.", link: "https://advancedscientificcalculator.vercel.app/", tag: "Utility", image: "/img/Calculator.png" },
  { name: "QuoteVerse", desc: "Dynamic quote fetcher built in React with category filters and sharing.", link: "https://quoteverse-one.vercel.app/", tag: "React", image: "/img/quoteverse.png" },
  { name: "Solaris To-Do", desc: "Task management with local persistence, priority tags, and animations.", link: "https://solaristodo.netlify.app/", tag: "Productivity", image: "/img/Solaris.png" },
];

const SKILL_CATEGORIES = [
  { id: "frontend", label: "Frontend", skills: ["React", "HTML5", "CSS3", "JavaScript", "TypeScript"] },
  { id: "backend", label: "Backend", skills: ["Node.js", "Express", "REST API", "OAuth 2.0"] },
  { id: "databases", label: "Databases", skills: ["MongoDB", "PostgreSQL"] },
  { id: "languages", label: "Languages", skills: ["JavaScript", "TypeScript", "Python", "Java"] },
  { id: "tools", label: "Tools", skills: ["Git", "Vercel", "Netlify"] },
  { id: "fundamentals", label: "CS Fundamentals", skills: ["DSA", "OOP"] },
];

const EDUCATION = [
  { year: "2024 – 2026", deg: "Master of Computer Applications", school: "Guru Jambheshwar University of Science & Technology", grade: "Pursuing" },
  { year: "2021 – 2024", deg: "Bachelor of Computer Applications", school: "MDU Rohtak", grade: "8.1 CGPA" },
];

export default function Work() {
  return (
    <div>
      {/* Intro section — 1 fade */}
      <FadeInView>
        <p className="section-number"><Typewriter text="[01] ./work --about" /></p>
        <h2 className="section-title">Work</h2>

        <p className="work-intro">
          I'm <strong>Saket Kumar</strong> — a passionate developer currently pursuing my <strong>MCA (2024–26)</strong> from Guru Jambheshwar University. I completed my <strong>BCA (2021–24)</strong> from MDU Rohtak with <strong>8.1 CGPA</strong>. I love crafting clean, performant web experiences and am always chasing the intersection of great design and solid engineering.
        </p>

        <div className="timeline">
          {EDUCATION.map((item, i) => (
            <div className="timeline-item" key={i}>
              <div className="timeline-dot" />
              <span className="timeline-year">{item.year}</span>
              <h3 className="timeline-title">{item.deg}</h3>
              <p className="timeline-sub">{item.school}</p>
              {item.grade && <span className="timeline-grade">{item.grade}</span>}
            </div>
          ))}
        </div>
      </FadeInView>

      {/* Skills + Projects + CTA — 1 fade */}
      <FadeInView delay={0.15}>
        <div className="ascii-div" aria-hidden="true">{"/* ───────────────────────────── stack ───────────────────────────── */"}</div>
        <div className="skills-section">
          <h3 className="display-medium" style={{ marginBottom: "0.8em" }}>$ ls ./skills</h3>
          <div className="skills-grid">
            {SKILL_CATEGORIES.map((cat) => (
              <div key={cat.id} className="skill-category">
                <div className="skill-category-head">{cat.label}</div>
                <div className="skill-items">
                  {cat.skills.map((s) => (
                    <span key={s} className="skill-item">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <h3 className="display-medium" style={{ marginBottom: "0.6em", marginTop: "2em" }}>$ ls ./projects</h3>

        <div className="projects-grid">
          {PROJECTS.map((proj, i) => (
            <a key={i} href={proj.link} target="_blank" rel="noreferrer" className="project-card">
              <div className="project-image-wrap">
                <img
                  src={proj.image}
                  alt={proj.name}
                  className="project-image"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </div>
              <div className="project-content">
                <div className="project-tag">{proj.tag}</div>
                <h3 className="project-name">{proj.name}</h3>
                <p className="project-desc">{proj.desc}</p>
                <div className="project-footer">
                  <span className="project-link">
                    ./open_project <IconArrow />
                  </span>
                  <IconExternal />
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="work-cta">
          <div className="ascii-div" aria-hidden="true">{"/* ───────────────────────────── eof ───────────────────────────── */"}</div>
          <a href="/resume.pdf" target="_blank" rel="noreferrer" className="resume-link" download>
            [ download_resume.pdf ↓ ]
          </a>
          <p style={{ marginTop: "2em", color: "var(--muted)", fontSize: "1rem", fontFamily: "var(--font-mono)" }}>
            {">_ "}Want to collaborate?{" "}
            <Link to="/contact" className="link-underline" style={{ color: "var(--fg)", fontWeight: 600 }}>
              ./get_in_touch →
            </Link>
          </p>
        </div>
      </FadeInView>
    </div>
  );
}
